import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import { Form, Formik } from 'formik'
import React, { useRef } from 'react'
import { AxiosResponse } from 'axios'
import { renderToStaticMarkup } from 'react-dom/server'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash/fp'
import { jsPDF } from 'jspdf'
import '../fonts/FSMeWeb-Regular'
import { OverviewInitialValues } from '../helpers'
import theme from '../theme'
import dayjs from 'dayjs'
import { useClaimant } from '../context/col'
import useAsync from '../hooks/useAsync'
import submit from '../services/case-report/submit'
import upload from '../services/upload/upload'
import { useStyles } from './styled'
import ReactHtmlParser from 'react-html-parser'
import clsx from 'clsx'

import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import { InsuranceOverview } from '../components/overview/InsuranceOverview'
import { ClaimantOverview } from '../components/overview/ClaimantOverview'
import { TripOverview } from '../components/overview/TripOverview'
import { HealthProblemOverview } from '../components/overview/HealthProblemOverview'
import { CancellationOverview } from '../components/overview/CancellationOverview'
import { TicketOverview } from '../components/overview/TicketOverview'
import { OtherDataOverview } from '../components/overview/OtherDataOverview'
import { BaggageOverview } from '../components/overview/BaggageOverview'
import { AccidentOverview } from '../components/overview/AccidentOverview'
import { LiabilityOverview } from '../components/overview/LiabilityOverview'
import { TreatmentExpensesOverview } from '../components/overview/TreatmentExpensesOverview'
import { OverviewSectionTitle } from '../components/overview/OverviewSectionTitle'

import FormControl from '@material-ui/core/FormControl'
import {
  CostRespType,
  TreatmentType,
  CaseReportId,
  CaseReportRespType,
} from '../models'
import Container from '@material-ui/core/Container'
import { useVocabulary } from '../context/vocabulary'
import useGetData from '../hooks/useGetData'
import { useStylesPdf } from './styledPdf'
import * as yup from 'yup'
import logo from '../images/ergo.png'
import ErrorSubmitBack from '../components/ErrorSubmitBack'
import { Ga4Form } from '../components/forms/Ga4Form'

export type OverviewPageProps = {
  transitions: OverviewPageTransitions
}

export type OverviewPageTransitions = {
  goBack: () => void
  goToNextPage: () => void
}

export type OverviewGeneratePDFProps = {
  caseReportId: CaseReportId
  loadedCaseReportResp: CaseReportRespType
}

const Overview = (props: OverviewPageProps) => {
  const classes = useStyles(theme)
  const classesPdf = useStylesPdf(theme)
  const { t } = useTranslation()
  const { state, dispatch } = useClaimant()
  const { vocabulary } = useVocabulary()
  /* FIXME: co s timto ? */

  const treatmentTypesArray = [
    { type: 'OP', name: t('thirdPg.treatmentTypes.type0') },
    { type: 'IP', name: t('thirdPg.treatmentTypes.type2') },
    { type: 'OTH', name: t('thirdPg.treatmentTypes.type3') },
  ]

  const { fetch: fetchData } = useGetData({})

  React.useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handlePageBack() {
    props.transitions.goBack()
  }

  function handleCaseReportSubmit() {
    caseReportSubmitAsyncFnc.fetch({
      caseReportId: state.caseReportId!,
      loadedCaseReportResp: state.loadedCaseReportResp,
    })
  }

  const caseReportSubmitAsyncFnc = useAsync({
    promiseFn: async (params: OverviewGeneratePDFProps) => {
      // 1. gen pdf
      const pdf = await generatePDF(params)
      // 2. uloz pdf do globalniho state
      dispatch({
        type: 'pdf',
        payload: { pdf: pdf },
      })

      // 3 - upload case document
      await uploadCaseReport({ caseReportId: params.caseReportId, pdf: pdf })
      // 4 - submit case report
      await submitCaseReport({ caseReportId: params.caseReportId! })
    },
    onData: () => {
      //fetchData() // tak to bylo; znovu načtení dat z API
      props.transitions.goToNextPage()
    },
    onError: (error) => {
      dispatch({ type: 'error', payload: { error: error.message } })
    },
  })

  const submitInProgress: boolean = caseReportSubmitAsyncFnc.pending

  const generatePDF = async (params: OverviewGeneratePDFProps) => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'px',
      format: 'a4',
      hotfixes: ['px_scaling'],
      putOnlyUsedFonts: true,
      floatPrecision: 16, // or "smart", default is 16
    })
    // generuje static-HTML kod
    const pdfPart = renderToStaticMarkup(
      CreateOverviewContent(classesPdf, params)
    )
    await pdf.html(pdfPart, {
      // Generate a PDF from an HTML element
      x: 40, // pozicovani
      autoPaging: 'text', // Trys not to cut text in half across page breaks. Works best for documents consisting mostly of a single column of text
      margin: [40, 0, 40, 0],
    })
    return pdf
  }

  const uploadCaseReport = async function ({
    caseReportId,
    pdf,
  }: {
    caseReportId: CaseReportId
    pdf: jsPDF
  }): Promise<AxiosResponse<any>> {
    const formData = new FormData()
    const blob = pdf.output('blob')
    const blobData = new Blob([blob], { type: 'application/pdf' })
    formData.append('document', blobData, 'CaseReport.pdf')
    return upload({
      // @ts-ignore
      formData: formData,
      name: 'CaseReport.pdf',
      id: caseReportId,
      type: 'CASEREPORT',
      itemOrderNum: 1,
      part: 'CASE',
    })
  }

  const submitCaseReport = async function ({
    caseReportId,
  }: {
    caseReportId: CaseReportId
  }): Promise<AxiosResponse<any>> {
    return submit({ id: caseReportId })
  }

  const OverviewSchema = () =>
    yup.object().shape({
      agree: yup.boolean().oneOf([true], t('sixthPg.agree.required')),
      agree1: yup.boolean().oneOf([true], t('sixthPg.agree.required')),
      agree2: yup.boolean().oneOf([true], t('sixthPg.agree.required')),
      agree3: yup.boolean().oneOf([true], t('sixthPg.agree.required')),
    })

  const CreateOverviewContent = (
    styleClasses: any,
    params: OverviewGeneratePDFProps
  ) => {
    const loadedCaseReportResp = params.loadedCaseReportResp!
    const caseType = loadedCaseReportResp.caseType
    return (
      <div id="#pdf" className={[styleClasses.pdf, 'cux-exclude'].filter(Boolean).join(' ')}>
        {' '}
        {/* TODO: proc pdf ? V id by taky nemel byt '#'. */}
        <Grid
          sm={12}
          xs={12}
          xl={12}
          container
          className={styleClasses.overviewContainer}
        >
          <Grid
            item
            xs={12}
            sm={12}
            xl={12}
            container
            className={styleClasses.centerRowOverview}
            spacing={2}
          >
            <img src={logo} className={styleClasses.ERGOLogo} alt="Page Logo" />
            <Typography className={styleClasses.textOverview} variant="h3">
              {t('sixthPg.title')}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            xl={12}
            container
            className={styleClasses.gridItem}
            spacing={2}
          >
            <Grid
              item
              xl={12}
              xs={12}
              sm={12}
              className={styleClasses.gridOverview}
              spacing={2}
            >
              <Grid
                item
                xs={12}
                sm={12}
                xl={12}
                container
                className={styleClasses.fieldSize}
              >
                <InsuranceOverview
                  sectionLabelStyles={styleClasses.textOverview}
                  caseType={caseType}
                  insuranceData={loadedCaseReportResp?.insurance}
                  t={t}
                />
              </Grid>
            </Grid>
          </Grid>
          {!isEmpty(loadedCaseReportResp?.trip) && (
            <Grid
              item
              xs={12}
              sm={12}
              xl={12}
              container
              className={styleClasses.gridOverview}
              spacing={2}
            >
              <Grid
                item
                xl={12}
                xs={12}
                sm={12}
                className={styleClasses.fieldSize}
              >
                <OverviewSectionTitle
                  styles={styleClasses.textOverview}
                  titleName={t('firstPg.details')}
                />
                <TripOverview
                  tripData={loadedCaseReportResp?.trip}
                  attachmentList={loadedCaseReportResp?.attachmentList}
                  t={t}
                />
              </Grid>
            </Grid>
          )}
          <Grid
            item
            xs={12}
            sm={12}
            xl={12}
            container
            className={styleClasses.gridOverview}
            spacing={2}
          >
            <Grid
              item
              xl={12}
              xs={12}
              sm={12}
              className={styleClasses.fieldSize}
            >
              <OverviewSectionTitle
                styles={styleClasses.textOverview}
                titleName={t('secondPg.title')}
              />
              <ClaimantOverview
                claimant={loadedCaseReportResp?.claimant}
                countries={vocabulary.countries}
                t={t}
              />
            </Grid>
          </Grid>
          {caseType === 'ME' && (
            <Grid
              item
              xs={12}
              sm={12}
              xl={12}
              container
              className={styleClasses.gridOverview}
              spacing={2}
            >
              <Grid
                item
                xl={12}
                xs={12}
                sm={12}
                className={styleClasses.fieldSize}
              >
                <OverviewSectionTitle
                  styles={styleClasses.textOverview}
                  titleName={t('thirdPg.title')}
                />
                <HealthProblemOverview
                  caseCommon={loadedCaseReportResp?.caseCommon}
                  caseME={loadedCaseReportResp?.caseME}
                  destinations={vocabulary.destinations}
                  t={t}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                xl={12}
                container
                className={styleClasses.gridOverview}
                spacing={2}
              >
                <Grid
                  item
                  xl={12}
                  xs={12}
                  sm={12}
                  className={styleClasses.fieldSize}
                >
                  {' '}
                  {/* FIXME: slozite a asi chybne */}
                  {loadedCaseReportResp?.caseME?.treatmentTypes?.map(
                    (treatmentType: TreatmentType) => {
                      return (
                        // @ts-ignore
                        loadedCaseReportResp?.caseME?.treatmentExpenses[
                          treatmentType
                        ]?.costs?.map((item: CostRespType, index: number) => {
                          const title = treatmentTypesArray.find(
                            (itemType) => itemType.type === treatmentType
                          )
                          return (
                            <React.Fragment key={index}>
                              {index === 0 && (
                                <OverviewSectionTitle
                                  styles={styleClasses.textOverview}
                                  titleName={t(`forthPg.title${title?.type}`)}
                                />
                              )}
                              <TreatmentExpensesOverview
                                treatmentType={treatmentType}
                                cost={item}
                                t={t}
                              />
                            </React.Fragment>
                          )
                        })
                      )
                    }
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
          {caseType === 'CA' && (
            <Grid
              item
              xs={12}
              sm={12}
              xl={12}
              container
              className={styleClasses.gridOverview}
              spacing={2}
            >
              <Grid
                item
                xl={12}
                xs={12}
                sm={12}
                className={styleClasses.fieldSize}
              >
                <CancellationOverview
                  caseCommon={loadedCaseReportResp?.caseCommon}
                  caseCA={loadedCaseReportResp?.caseCA}
                  destinations={vocabulary.destinations}
                  sectionLabelStyles={styleClasses.textOverview}
                  t={t}
                />
              </Grid>
            </Grid>
          )}
          {caseType === 'BA' && (
            <Grid
              item
              xs={12}
              sm={12}
              xl={12}
              container
              className={styleClasses.gridOverview}
              spacing={2}
            >
              <Grid
                item
                xl={12}
                xs={12}
                sm={12}
                className={styleClasses.fieldSize}
              >
                <OverviewSectionTitle
                  styles={styleClasses.textOverview}
                  titleName={t('baggageForm.title')}
                />
                <BaggageOverview
                  caseCommon={loadedCaseReportResp?.caseCommon}
                  caseBA={loadedCaseReportResp?.caseBA}
                  destinations={vocabulary.destinations}
                  t={t}
                />
              </Grid>
            </Grid>
          )}
          {caseType === 'PL' && (
            <Grid
              item
              xs={12}
              sm={12}
              xl={12}
              container
              className={styleClasses.gridOverview}
              spacing={2}
            >
              <Grid
                item
                xl={12}
                xs={12}
                sm={12}
                className={styleClasses.fieldSize}
              >
                <OverviewSectionTitle
                  styles={styleClasses.textOverview}
                  titleName={t('liabilityForm.title')}
                />
                <LiabilityOverview
                  caseCommon={loadedCaseReportResp?.caseCommon}
                  casePL={loadedCaseReportResp?.casePL}
                  destinations={vocabulary.destinations}
                  sectionLabelStyles={styleClasses.textOverview}
                  t={t}
                />
              </Grid>
            </Grid>
          )}
          {caseType === 'PA' && (
            <Grid
              item
              xs={12}
              sm={12}
              xl={12}
              container
              className={styleClasses.gridOverview}
              spacing={2}
            >
              <Grid
                item
                xl={12}
                xs={12}
                sm={12}
                className={styleClasses.fieldSize}
              >
                <OverviewSectionTitle
                  styles={styleClasses.textOverview}
                  titleName={t('accidentForm.title')}
                />
                <AccidentOverview
                  caseCommon={loadedCaseReportResp?.caseCommon}
                  casePA={loadedCaseReportResp?.casePA}
                  destinations={vocabulary.destinations}
                  t={t}
                />
              </Grid>
            </Grid>
          )}
          {caseType === 'TI' && (
            <Grid
              item
              xs={12}
              sm={12}
              xl={12}
              container
              className={styleClasses.gridOverview}
              spacing={2}
            >
              <Grid
                item
                xl={12}
                xs={12}
                sm={12}
                className={styleClasses.fieldSize}
              >
                <OverviewSectionTitle
                  styles={styleClasses.textOverview}
                  titleName={t('ticketForm.title')}
                />
                <TicketOverview
                  caseCommon={loadedCaseReportResp?.caseCommon}
                  caseTI={loadedCaseReportResp?.caseTI}
                  t={t}
                />
              </Grid>
            </Grid>
          )}
          <Grid
            item
            xs={12}
            sm={12}
            xl={12}
            container
            className={styleClasses.gridOverview}
            spacing={2}
          >
            <Grid
              item
              xl={12}
              xs={12}
              sm={12}
              className={styleClasses.fieldSize}
            >
              <OtherDataOverview
                sectionLabelStyles={styleClasses.textOverview}
                caseType={caseType}
                otherData={loadedCaseReportResp?.other}
                t={t}
              />
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            xl={12}
            container
            className={classes.gridOverview}
            spacing={2}
          >
            <Grid item xl={12} xs={12} sm={12} className={classes.fieldSize}>
              <div className={classesPdf.paragraphText}>
                {t('sixthPg.date') + dayjs(new Date()).format('DD.MM.YYYY')}
              </div>
            </Grid>
          </Grid>
        </Grid>
      </div>
    )
  }

  const formikRef = useRef(null)
  return (
    <Ga4Form formName={'TripForm'} >
      <Formik
        initialValues={OverviewInitialValues}
        validationSchema={OverviewSchema}
        onSubmit={() => {
          handleCaseReportSubmit()
        }}
        innerRef={formikRef}
      >
        {({ values, errors, handleChange, touched }) => {
          return (
            <Grid className={classes.root}>
              <Form>
                <Grid
                  item
                  sm={12}
                  xl={12}
                  xs={12}
                  container
                  direction="row"
                  className={classes.gridMainForm}
                >
                  <Grid
                    item
                    sm={12}
                    xl={12}
                    xs={12}
                    container
                    direction="row"
                    className={classes.gridWorkArea}
                  >
                    {CreateOverviewContent(classes, {
                      caseReportId: state.caseReportId!,
                      loadedCaseReportResp: state.loadedCaseReportResp!,
                    })}
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridOverview}
                      spacing={2}
                    >
                      <Grid
                        item
                        xl={12}
                        xs={12}
                        sm={12}
                        className={classes.fieldSize}
                      >
                        <Typography className={classes.defaultText}>
                          {t('sixthPg.caution')}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Container className={classes.overviewBoxRoot}>
                      <Grid
                        container
                        direction="column"
                        justify="flex-start"
                        className={classes.overviewBoxContainer}
                      >
                        <Grid container item xs={12} xl={12} sm={12}>
                          <Grid
                            item
                            xs={1}
                            xl={1}
                            sm={1}
                            className={classes.labelControl}
                          >
                            <FormControl component="fieldset">
                              <FormControlLabel
                                name="agree"
                                checked={values.agree}
                                onChange={handleChange}
                                control={<Checkbox />}
                                label=""
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={11} xl={11} sm={11}>
                            <Typography className={classes.defaultText}>
                              {ReactHtmlParser(t('sixthPg.textBox1'))}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid item xs={12} xl={12} sm={12}>
                          {errors.agree && touched.agree && (
                            <div
                              className={clsx(
                                classes.checkError,
                                classes.checkErrorOverview
                              )}
                            >
                              {errors.agree}
                            </div>
                          )}
                        </Grid>
                      </Grid>
                    </Container>
                    <Container className={classes.overviewBoxRoot}>
                      <Grid
                        container
                        direction="column"
                        justify="flex-start"
                        className={classes.overviewBoxContainer}
                      >
                        <Grid container item xs={12} xl={12} sm={12}>
                          <Grid
                            item
                            xs={1}
                            xl={1}
                            sm={1}
                            className={classes.labelControl}
                          >
                            <FormControl component="fieldset">
                              <FormControlLabel
                                name="agree1"
                                checked={values.agree1}
                                onChange={handleChange}
                                control={<Checkbox />}
                                label=""
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={11} xl={11} sm={11}>
                            <Typography className={classes.defaultText}>
                              {ReactHtmlParser(t('sixthPg.textBox2'))}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid item xs={12} xl={12} sm={12}>
                          {errors.agree1 && touched.agree1 && (
                            <div
                              className={clsx(
                                classes.checkError,
                                classes.checkErrorOverview
                              )}
                            >
                              {errors.agree1}
                            </div>
                          )}
                        </Grid>
                      </Grid>
                    </Container>
                    <Container className={classes.overviewBoxRoot}>
                      <Grid
                        container
                        direction="column"
                        justify="flex-start"
                        className={classes.overviewBoxContainer}
                      >
                        <Grid container item xs={12} xl={12} sm={12}>
                          <Grid
                            item
                            xs={1}
                            xl={1}
                            sm={1}
                            className={classes.labelControl}
                          >
                            <FormControl component="fieldset">
                              <FormControlLabel
                                name="agree2"
                                checked={values.agree2}
                                onChange={handleChange}
                                control={<Checkbox />}
                                label=""
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={11} xl={11} sm={11}>
                            <Typography className={classes.defaultText}>
                              {t('sixthPg.textBox3')}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid item xs={12} xl={12} sm={12}>
                          {errors.agree2 && touched.agree2 && (
                            <div
                              className={clsx(
                                classes.checkError,
                                classes.checkErrorOverview
                              )}
                            >
                              {errors.agree2}
                            </div>
                          )}
                        </Grid>
                      </Grid>
                    </Container>
                    <Container className={classes.overviewBoxRoot}>
                      <Grid
                        container
                        direction="column"
                        justify="flex-start"
                        className={classes.overviewBoxContainer}
                      >
                        <Grid container item xs={12} xl={12} sm={12}>
                          <Grid
                            item
                            xs={1}
                            xl={1}
                            sm={1}
                            className={classes.labelControl}
                          >
                            <FormControl component="fieldset">
                              <FormControlLabel
                                name="agree3"
                                checked={values.agree3}
                                onChange={handleChange}
                                control={<Checkbox />}
                                label=""
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={11} xl={11} sm={11}>
                            <Typography className={classes.defaultText}>
                              {t('sixthPg.textBox4')}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid item xs={12} xl={12} sm={12}>
                          {errors.agree3 && touched.agree3 && (
                            <div
                              className={clsx(
                                classes.checkError,
                                classes.checkErrorOverview
                              )}
                            >
                              {errors.agree3}
                            </div>
                          )}
                        </Grid>
                      </Grid>
                    </Container>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        xl={12}
                        xs={12}
                        sm={12}
                        className={classes.gridCenter}
                      >
                        <ErrorSubmitBack
                          formik={formikRef.current}
                          pending={submitInProgress}
                          backLinkText={t('sixthPg.backBtn')}
                          onBackLinkClick={() => {
                            handlePageBack()
                          }}
                        >
                          {t('sixthPg.continueBtn')}
                        </ErrorSubmitBack>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs sm={3} xl={3} />
                </Grid>
              </Form>
            </Grid>
          )
        }}
      </Formik>
    </Ga4Form >
  )
}

export default Overview
